/**
 * Notification dialog — the customer fan-out of a BETROFFEN event.
 *
 * `POST …/{id}/email` is MUTATING despite its name: it (re-)runs the
 * Benachrichtigung fan-out (one card per affected ProduktInstanz) and only then
 * drafts one e-mail per Kunde. That is why it runs when the dialog opens rather
 * than on render, and why it is guarded against React's StrictMode double-mount:
 * a second POST would be harmless but would log a second fan-out run.
 *
 * Nothing is really sent in this MVP — the operator opens the draft in their mail
 * client (`mailto:`) and then stamps the Benachrichtigung cards as sent, which is
 * the record the CRA audit relies on.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { CveText } from '../../components/CveLink';
import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import Spinner, { PageSpinner } from '../../components/Spinner';
import {
  ApiError,
  errorMessage,
  fetchEmailDrafts,
  markBenachrichtigungVersendet,
} from '../../lib/api';
import { fmtValue } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type {
  EmailDraft,
  EmailDraftsResponse,
  EmailDraftsResult,
  VulnerabilityEventRow,
} from '../../lib/types';

import { benachrichtigungIds, offeneBenachrichtigungIds } from './eventHelpers';

export interface NotificationDialogProps {
  event: VulnerabilityEventRow;
  /** `notification.send` — false means read-only: drafts visible, no stamping. */
  canSend: boolean;
  onClose: () => void;
  /** Called after every successful stamp so the parent can refetch the row. */
  onStamped: () => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; result: EmailDraftsResult }
  | { kind: 'error'; message: string; status: number | null };

export function NotificationDialog({
  event,
  canSend,
  onClose,
  onStamped,
}: NotificationDialogProps): ReactNode {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  /** Benachrichtigung ids stamped in this dialog — server-confirmed, not optimistic. */
  const [stamped, setStamped] = useState<ReadonlySet<number>>(new Set());
  const [markingKunde, setMarkingKunde] = useState<number | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);
  /**
   * The in-flight (or completed) fan-out request, keyed by event.
   *
   * StrictMode mounts effects twice in development, and this POST re-runs the
   * Benachrichtigung fan-out — so it must fire exactly once. Guarding with a
   * plain "already started" flag is NOT enough: the first cleanup would cancel
   * the only subscription while the second pass skips the request altogether,
   * leaving the dialog on its spinner forever. Caching the promise instead lets
   * the second pass re-subscribe to the same request.
   */
  const request = useRef<{ eventId: number; promise: Promise<EmailDraftsResponse> } | null>(null);

  useEffect(() => {
    const eventId = event.id;
    let cancelled = false;
    const cached = request.current;
    const promise =
      cached !== null && cached.eventId === eventId ? cached.promise : fetchEmailDrafts(eventId);
    request.current = { eventId, promise };
    promise.then(
      (response) => {
        if (!cancelled) setState({ kind: 'ready', result: response.result });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: errorMessage(err),
          status: err instanceof ApiError ? err.status : null,
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [event.id]);

  const markDraft = useCallback(
    async (draft: EmailDraft): Promise<void> => {
      const ids = offeneBenachrichtigungIds(draft, stamped);
      if (ids.length === 0) return;
      setMarkingKunde(draft.kunde.id);
      setMarkError(null);
      const confirmed: number[] = [];
      try {
        // Sequential on purpose: the first failure must stop the round and be
        // reported with the server's German detail instead of half-applying.
        for (const id of ids) {
          const response = await markBenachrichtigungVersendet(id, {
            empfaenger: draft.kunde.email ?? '',
          });
          confirmed.push(response.result.id);
        }
      } catch (err) {
        setMarkError(errorMessage(err));
      } finally {
        if (confirmed.length > 0) {
          setStamped((current) => {
            const next = new Set(current);
            for (const id of confirmed) next.add(id);
            return next;
          });
          onStamped();
        }
        setMarkingKunde(null);
      }
    },
    [stamped, onStamped],
  );

  const busy = markingKunde !== null;

  return (
    <Modal
      title="Kundenbenachrichtigung"
      subtitle={
        state.kind === 'ready'
          ? <CveText text={`Event #${event.id} · ${state.result.cve || 'CVE unbekannt'} · Entwürfe, kein realer Versand`} />
          : <CveText text={`Event #${event.id} · ${event.cve.id ?? 'CVE unbekannt'}`} />
      }
      onClose={onClose}
      size="wide"
      dismissable={!busy}
      footer={
        <>
          <div className="spacer" />
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>
            Schließen
          </button>
        </>
      }
    >
      {state.kind === 'loading' ? (
        <PageSpinner text="Benachrichtigungen werden erzeugt…" />
      ) : state.kind === 'error' ? (
        <Banner
          kind="error"
          sub={
            state.status === 409
              ? 'Bewerten Sie den Event zuerst als „Betroffen“.'
              : state.status === 404
                ? 'Der Event existiert nicht mehr, laden Sie die Liste neu.'
                : 'Es wurde keine Benachrichtigung erzeugt.'
          }
        >
          {state.message}
        </Banner>
      ) : (
        <>
          {markError === null ? null : (
            <Banner kind="error" sub="Nicht alle Benachrichtigungen wurden als versendet markiert.">
              {markError}
            </Banner>
          )}

          {state.result.drafts.length === 0 ? (
            <Banner kind="warn">
              Keine betroffenen Kunden: Nach bekannter Konfiguration läuft keine Produktinstanz mit
              dieser Komponentenversion.
            </Banner>
          ) : (
            <Banner kind="info">
              {state.result.drafts.length}{' '}
              {state.result.drafts.length === 1 ? 'Kunde' : 'Kunden'} betroffen, ein Entwurf pro
              Kunde. Versenden Sie die Entwürfe im eigenen Mailprogramm und markieren Sie sie danach
              hier als versendet.
            </Banner>
          )}

          {state.result.drafts.map((draft) => {
            const allIds = benachrichtigungIds(draft);
            const openIds = offeneBenachrichtigungIds(draft, stamped);
            const marking = markingKunde === draft.kunde.id;
            return (
              <article className="mail" key={draft.kunde.id}>
                <div className="to">
                  <strong>{fmtValue(draft.kunde.name)}</strong> · An:{' '}
                  {draft.kunde.email ? (
                    draft.kunde.email
                  ) : (
                    <span className="muted">(keine E-Mail hinterlegt)</span>
                  )}{' '}
                  · {draft.instanzen.length} Instanz(en)
                </div>
                <div className="subj">{draft.betreff}</div>
                <pre>{draft.inhalt}</pre>
                <div className="triage-mail-foot">
                  <ChipList>
                    {allIds.length === 0 ? (
                      <Chip tone="warn">Keine Benachrichtigung angelegt</Chip>
                    ) : (
                      <Chip mono>
                        Benachrichtigung {allIds.map((id) => `#${id}`).join(', ')}
                      </Chip>
                    )}
                    {openIds.length === 0 ? (
                      <Chip tone="good">versendet</Chip>
                    ) : (
                      <Chip tone="warn">{openIds.length} offen</Chip>
                    )}
                  </ChipList>
                  <div className="spacer" />
                  {draft.mailto === null ? (
                    <span className="muted"><Icon name="warning" size={14} /> Keine Kontakt-E-Mail hinterlegt</span>
                  ) : (
                    <a href={draft.mailto}>
                      <strong><Icon name="mail" /> Im Mailprogramm öffnen</strong>
                    </a>
                  )}
                  {openIds.length === 0 ? null : canSend ? (
                    <button
                      type="button"
                      className="primary small"
                      onClick={() => void markDraft(draft)}
                      disabled={busy}
                    >
                      {marking ? <Spinner variant="on-accent" /> : null} Als versendet markieren
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="primary small"
                      disabled
                      title={missingPermissionTitle('notification.send')}
                    >
                      Als versendet markieren
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </>
      )}
    </Modal>
  );
}

export default NotificationDialog;
